import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const PHYSARUM_PARAMS = [
  {
    kind: 'int',
    key: 'agents',
    label: 'Agenten',
    min: 5000,
    max: 2000000,
    step: 1000,
    scale: 'log',
    // Kept modest by default: the diffusion pass is O(grid) per step, so the product of
    // agents, grid size and steps is what decides whether a run takes a second or a minute.
    default: 90000,
    bulkRange: [25000, 250000],
    group: 'Agenten',
  },
  {
    kind: 'float',
    key: 'sensorAngle',
    label: 'Sensorwinkel',
    min: 2,
    max: 120,
    step: 0.5,
    default: 26,
    unit: '°',
    group: 'Agenten',
  },
  {
    kind: 'float',
    key: 'sensorDistance',
    label: 'Sensordistanz',
    min: 1,
    max: 60,
    step: 0.5,
    default: 11,
    unit: 'px',
    group: 'Agenten',
  },
  {
    kind: 'float',
    key: 'turnSpeed',
    label: 'Drehgeschwindigkeit',
    min: 1,
    max: 90,
    step: 0.5,
    default: 22,
    unit: '°',
    group: 'Agenten',
  },
  {
    kind: 'float',
    key: 'stepSize',
    label: 'Schrittgrösse',
    min: 0.2,
    max: 8,
    step: 0.1,
    default: 1.1,
    unit: 'px',
    group: 'Agenten',
  },
  {
    kind: 'float',
    key: 'randomTurn',
    label: 'Zufallsdrehung',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.08,
    group: 'Agenten',
  },

  {
    kind: 'float',
    key: 'deposit',
    label: 'Ablagerungsrate',
    min: 0.01,
    max: 1,
    step: 0.01,
    default: 0.32,
    group: 'Pheromon',
  },
  {
    kind: 'float',
    key: 'diffusion',
    label: 'Diffusionsrate',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.42,
    group: 'Pheromon',
  },
  {
    kind: 'float',
    key: 'decay',
    label: 'Verdunstungsrate',
    min: 0.001,
    max: 0.2,
    step: 0.001,
    default: 0.026,
    precision: 3,
    group: 'Pheromon',
  },

  {
    kind: 'enum',
    key: 'textRole',
    label: 'Rolle des Textes',
    default: 'nutrient',
    group: 'Text',
    options: [
      { value: 'nutrient', label: 'Nährstoffquelle' },
      { value: 'obstacle', label: 'Hindernis' },
      { value: 'spawn', label: 'Startverteilung' },
      { value: 'none', label: 'Ohne Wirkung' },
    ],
  },
  {
    kind: 'float',
    key: 'textStrength',
    label: 'Textstärke',
    min: 0,
    max: 2,
    step: 0.01,
    default: 0.55,
    group: 'Text',
    showIf: (v) => v.textRole !== 'none',
  },

  {
    kind: 'int',
    key: 'resolution',
    label: 'Simulationsauflösung',
    min: 256,
    max: 2048,
    step: 32,
    default: 440,
    unit: 'px',
    neverRandomise: true,
    group: 'Lauf',
  },
  {
    kind: 'int',
    key: 'totalSteps',
    label: 'Gesamtschritte',
    min: 50,
    max: 6000,
    step: 10,
    default: 300,
    bulkRange: [150, 700],
    group: 'Lauf',
  },
  {
    kind: 'int',
    key: 'stepsPerFrame',
    label: 'Iterationen pro Frame',
    min: 1,
    max: 60,
    step: 1,
    default: 12,
    neverRandomise: true,
    group: 'Lauf',
  },

  {
    kind: 'palette',
    key: 'palette',
    label: 'Farbrampe',
    default: { ...DEFAULT_PALETTE, paletteId: 'ember' },
    group: 'Farbe',
  },
  {
    kind: 'float',
    key: 'contrast',
    label: 'Kontrast',
    min: 0.2,
    max: 8,
    step: 0.05,
    default: 2.2,
    group: 'Farbe',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 1,
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type PhysarumValues = SchemaValues<typeof PHYSARUM_PARAMS>;
